import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';

import { AdminPageHeader } from '../components/layout/AdminPageHeader';
import { Card, Badge, Button, Spinner } from '../components/ui';
import { adminApi } from '../services/api';

const StatCard = ({ title, value, icon, colorClass, delay, suffix }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5 }}
  >
    <Card className="p-8 rounded-[40px] shadow-xl relative overflow-hidden group border-none">
      <div className={`absolute top-0 right-0 w-32 h-32 -mr-10 -mt-10 rounded-full ${colorClass.bg} opacity-20 group-hover:scale-125 transition-transform duration-700 pointer-events-none`} />
      <div className={`w-14 h-14 rounded-2xl ${colorClass.box} flex items-center justify-center shadow-inner mb-6 relative z-10`}>
        <span
          className={`material-symbols-outlined text-[28px] ${colorClass.text}`}
          style={{ fontVariationSettings: "'FILL' 1" }}
        >
          {icon}
        </span>
      </div>
      <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] italic mb-1 relative z-10">
        {title}
      </div>
      <div className="text-4xl font-black text-slate-900 dark:text-white italic tracking-tighter relative z-10">
        {value !== undefined && value !== null ? (
          <>
            {value}
            {suffix ? (
              <span className="text-[10px] uppercase font-black not-italic text-slate-400 ml-2">
                {suffix}
              </span>
            ) : null}
          </>
        ) : (
          <Spinner size="sm" />
        )}
      </div>
    </Card>
  </motion.div>
);

const AdminStatistics = () => {
  const { t } = useTranslation();
  const isDark = false;
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsData, analyticsData] = await Promise.all([
        adminApi.getStats(),
        adminApi.getAnalytics(),
      ]);
      setStats(statsData);
      setAnalytics(Array.isArray(analyticsData) ? analyticsData : []);
    } catch {
      setStats(null);
      setAnalytics([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="h-[60vh] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const revenueCategories = analytics.map((d) => d.date?.split(',')[0] || d.date || '');
  const revenueSeries = analytics.map((d) => d.revenue ?? d.total ?? 0);
  const ordersSeries = analytics.map((d) => d.orders ?? d.count ?? 0);

  return (
    <div className="space-y-10 pb-10">
      <AdminPageHeader
        tag="ANALYTICS"
        title={t('nav.statistics') || 'Statistika'}
        subtitle={t('dashboard.statisticsSubtitle') || "Tizim ko'rsatkichlari va grafiklar"}
        icon="bar_chart"
      >
        <Button
          variant="ghost"
          onClick={fetchData}
          className="h-14 w-14 rounded-3xl p-0 hover:bg-[#1e9f6b]/10 hover:text-[#1e9f6b] transition-colors"
        >
          <span className="material-symbols-outlined">refresh</span>
        </Button>
      </AdminPageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t('dashboard.todayOrders') || 'Bugungi buyurtmalar'}
          value={stats?.totalOrdersToday}
          icon="shopping_cart"
          colorClass={{ bg: 'bg-blue-500', box: 'bg-blue-500/10', text: 'text-blue-600' }}
          delay={0.1}
        />
        <StatCard
          title={t('dashboard.activeDrivers') || 'Faol haydovchilar'}
          value={stats?.activeDrivers}
          icon="local_shipping"
          colorClass={{ bg: 'bg-emerald-500', box: 'bg-emerald-500/10', text: 'text-emerald-600' }}
          delay={0.2}
        />
        <StatCard
          title={t('dashboard.pendingOrders') || 'Kutilayotgan'}
          value={stats?.pendingOrders}
          icon="hourglass_empty"
          colorClass={{ bg: 'bg-amber-500', box: 'bg-amber-500/10', text: 'text-amber-600' }}
          delay={0.3}
        />
        <StatCard
          title={t('dashboard.todayRevenue') || 'Bugungi daromad'}
          value={stats?.revenueToday?.toLocaleString?.() ?? stats?.revenueToday}
          icon="payments"
          colorClass={{ bg: 'bg-indigo-500', box: 'bg-indigo-500/10', text: 'text-indigo-600' }}
          suffix={t('common.uzs') || 'UZS'}
          delay={0.4}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}>
          <Card className="p-8 rounded-[40px] shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-[0.3em] italic flex items-center gap-3">
                <div className="h-8 w-8 bg-indigo-500/10 rounded-xl flex items-center justify-center text-indigo-500">
                  <span
                    className="material-symbols-outlined text-[18px]"
                    style={{ fontVariationSettings: "'FILL' 1" }}
                  >
                    payments
                  </span>
                </div>
                {t('dashboard.weeklyRevenue') || 'Haftalik daromad'}
              </h3>
              <Badge variant="slate" className="rounded-xl font-black italic uppercase text-[9px] tracking-widest">
                {t('dashboard.last7Days') || '7 kun'}
              </Badge>
            </div>
            <div className="h-72">
              {revenueCategories.length > 0 ? (
                <Chart
                  options={{
                    chart: { type: 'area', toolbar: { show: false }, background: 'transparent' },
                    stroke: { curve: 'smooth', width: 4, colors: ['#4f46e5'] },
                    fill: {
                      type: 'gradient',
                      gradient: {
                        shadeIntensity: 1,
                        opacityFrom: 0.45,
                        opacityTo: 0.05,
                        stops: [20, 100, 100, 100],
                      },
                    },
                    xaxis: { categories: revenueCategories, labels: { style: { colors: isDark ? '#94a3b8' : '#64748b' } } },
                    yaxis: { labels: { style: { colors: isDark ? '#94a3b8' : '#64748b' } } },
                    grid: { borderColor: isDark ? '#334155' : '#e2e8f0', strokeDashArray: 4 },
                    theme: { mode: isDark ? 'dark' : 'light' },
                    dataLabels: { enabled: false },
                  }}
                  series={[{ name: t('adminStatistics.revenue'), data: revenueSeries }]}
                  type="area"
                  height="100%"
                />
              ) : (
                <p className="text-slate-400 text-sm font-bold italic uppercase tracking-widest text-center py-20">
                  Ma&apos;lumot yo&apos;q
                </p>
              )}
            </div>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}>
          <Card className="p-8 rounded-[40px] shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-[0.3em] italic flex items-center gap-3">
                <div className="h-8 w-8 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-500">
                  <span
                    className="material-symbols-outlined text-[18px]"
                    style={{ fontVariationSettings: "'FILL' 1" }}
                  >
                    shopping_cart
                  </span>
                </div>
                {t('dashboard.weeklyOrders') || 'Haftalik buyurtmalar'}
              </h3>
              <Badge variant="slate" className="rounded-xl font-black italic uppercase text-[9px] tracking-widest">
                {t('dashboard.last7Days') || '7 kun'}
              </Badge>
            </div>
            <div className="h-72">
              {revenueCategories.length > 0 ? (
                <Chart
                  options={{
                    chart: { type: 'bar', toolbar: { show: false }, background: 'transparent' },
                    plotOptions: { bar: { borderRadius: 8, columnWidth: '55%' } },
                    colors: ['#1e9f6b'],
                    xaxis: { categories: revenueCategories, labels: { style: { colors: isDark ? '#94a3b8' : '#64748b' } } },
                    yaxis: { labels: { style: { colors: isDark ? '#94a3b8' : '#64748b' } } },
                    grid: { borderColor: isDark ? '#334155' : '#e2e8f0', strokeDashArray: 4 },
                    theme: { mode: isDark ? 'dark' : 'light' },
                    dataLabels: { enabled: false },
                  }}
                  series={[{ name: t('adminStatistics.orders'), data: ordersSeries }]}
                  type="bar"
                  height="100%"
                />
              ) : (
                <p className="text-slate-400 text-sm font-bold italic uppercase tracking-widest text-center py-20">
                  Ma&apos;lumot yo&apos;q
                </p>
              )}
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminStatistics;
